import React, { useEffect, useState } from "react";
import debounce from "lodash/debounce";
import toast from "react-hot-toast";

import { useDispatch } from "react-redux";
import { getCompanyClaims } from "../../redux/claims/actions";

import { PUT_CLAIM, ASSIGN_CLAIM, UNASSIGN_CLAIM, SET_CLAIMABLE } from "../../api/claims";
import { UseCompanyClaims } from "../../hooks/claims";

import ClaimNewCard from "./ClaimNewCard";
import NoClaims from "./NoClaims";
import { T, CLAIM_FONT } from "./claimTokens";

import SoftBox from "../../components/SoftBox";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";
import PageHeader from "../../components/PageHeader";
import { useMediaQuery, Modal, Backdrop, Fade } from "@mui/material";
import SoftTypography from "../../components/SoftTypography";
import SoftButton from "../../components/SoftButton";
import CloseIcon from "@mui/icons-material/Close";
import SoftSelect from "../../components/SoftSelect";
import { UseSelectedWorkspace, UseCompany } from "../../hooks/company";
import { GET_USER_BY_GROUPID } from "../../api/user";

const ClaimsPage = () => {
  const items = UseCompanyClaims();
  const [filteredItems, setFilteredItems] = useState(false);
  const [unclaimedOnly, setUnclaimedOnly] = useState(true);
  const [tabValue, setTabValue] = useState(0);
  const dispatch = useDispatch();

  const setClaimsFilter = (value) => {
    setTabValue(value);
    setUnclaimedOnly(!value);
  };

  useEffect(() => {
    dispatch(getCompanyClaims());
  }, []);

  useEffect(() => {
    if (items.length) {
      if (!unclaimedOnly) {
        setFilteredItems(items);
      } else {
        // Unclaimed tab: just reviews without an owner yet. Keep
        // admin-dismissed (claimable === false) reviews in this list too —
        // the card marks them "Not claimable" instead of hiding them, so an
        // admin isn't left wondering why a review vanished from Unclaimed.
        const tempItems = items.filter(
          (item) => !Array.isArray(item.kpi) || !item.kpi.some((k) => k.ownerId)
        );
        setFilteredItems(tempItems);
      }
    }
  }, [items, unclaimedOnly]);

  // Call the API directly and report the REAL outcome. The previous version
  // dispatched the redux saga (which swallows errors) and showed a fake
  // "Claimed Successfully" toast on a timer — so a failed claim (e.g. a 400
  // from the server) still looked successful, and the player only found out
  // when their points never appeared.
  const handleClaim = debounce(async (id, claimId, type) => {
    const claiming = toast.loading("Processing claim...");
    try {
      await PUT_CLAIM(id, claimId, type);
      toast.success("Claimed Successfully", { id: claiming });
    } catch (error) {
      console.error("Error claiming review:", error);
      const message =
        error?.response?.data?.error?.message || "Failed to claim review.";
      toast.error(message, { id: claiming });
    } finally {
      // Refresh either way: on success the card flips to Claimed with the real
      // owner; on failure the optimistic Claimed state reverts.
      dispatch(getCompanyClaims());
    }
  }, 500);

  // Light "frosted" surface, shared with the ClaimNewCard confirm dialogs so
  // every modal on the Claims page reads as one system.
  const styleModal = {
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    fontFamily: CLAIM_FONT,
    background:
      "linear-gradient(180deg, rgba(255,255,255,0.9) 0%, rgba(255,255,255,0.82) 100%)",
    backdropFilter: "blur(20px) saturate(160%)",
    WebkitBackdropFilter: "blur(20px) saturate(160%)",
    border: "1px solid rgba(255,255,255,0.6)",
    borderRadius: "20px",
    boxShadow:
      "0 24px 48px -12px rgba(16,24,40,0.22), inset 0 1px 0 rgba(255,255,255,0.7)",
    maxWidth: { mini: "calc(100% - 32px)", sm: "460px" },
    width: "100%",
    overflow: "visible",
  };

  const [assignModal, setAssignModal] = useState(false);
  const [users, setUsers] = useState([]);
  const [claimId, setClaimId] = useState(null); 
  const [selectedPlayer, setSelectedPlayer] = useState([]);
  // Guards the assign Submit button — a double-click used to fire the assign
  // mutation twice and credit the same review to the player twice.
  const [isAssigning, setIsAssigning] = useState(false);
  const workspace = UseSelectedWorkspace();
  const { _id: companyId } = UseCompany();
  const [dialog, setDialog] = useState(false);

  const fetchUsersByGroupId = async () => {
    try {
      let filteredUsers = [];
      const groupId = workspace._id;

      const { data } = await GET_USER_BY_GROUPID(groupId);

      // Show every real member of the workspace. Do NOT gate on `logins > 0`:
      // technicians synced from the CRM (e.g. Housecall Pro) often haven't
      // logged into GMB yet, but reviews still need to be assignable to them.
      filteredUsers = data.filter(
        (player) =>
          groupId === player.groupId &&
          !player.isArchived &&
          player.companyId !== null,
      );
      
        const userOptions = filteredUsers.map((item) => {
          const username = `${item.firstName} ${item.lastName}`;

          return {
            ...item,
            value: item._id,
            label: username,
          };
        });

        setUsers([...userOptions]);
    } catch (error) {
      console.log(error.response);
    }
  };

  useEffect(() => {
    fetchUsersByGroupId();
  }, []);


  const handleCloseModal = () => {
    setAssignModal(false);
    setClaimId(null);
  };

  const assignPopUp = debounce((id) => { 
    setClaimId(id);
    setAssignModal(true);
  }, 500);

  const assignClaim = async () => {
    if (isAssigning) return;
    if (!selectedPlayer || selectedPlayer.length === 0) {
      toast.error("Please select at least one player.");
      return;
    }
    const userIds = selectedPlayer.map(player => player.value);
    const assigning = toast.loading("Assigning claim...");
    setIsAssigning(true);
    try {
      await ASSIGN_CLAIM({
        claimId,
        userId: userIds,
        groupId: workspace._id,
        companyId
      });
      toast.success("Claim assigned successfully", { id: assigning });
      setDialog(true);
      setAssignModal(false);
    } catch (error) {
      console.error("Error assigning claim:", error);
      const message =
        error?.response?.data?.error?.message || "Failed to assign claim.";
      toast.error(message, { id: assigning });
    } finally {
      setIsAssigning(false);
    }
  };

  const handleUnassign = async (id) => {
    const unassigning = toast.loading("Unassigning claim...");
    try {
      await UNASSIGN_CLAIM({
        claimId: id,
        groupId: workspace._id,
        companyId,
      });
      toast.success("Review unassigned", { id: unassigning });
      // Refresh so the card returns to the claimable state.
      setTimeout(() => window.location.reload(), 800);
    } catch (error) {
      console.error("Error unassigning claim:", error);
      toast.error("Failed to unassign review", { id: unassigning });
    }
  };

  const handleSetClaimable = async (id, claimable) => {
    const saving = toast.loading(
      claimable ? "Restoring review..." : "Removing from claims..."
    );
    try {
      await SET_CLAIMABLE({ reviewId: id, claimable });
      toast.success(
        claimable ? "Review is claimable again" : "Review marked not claimable",
        { id: saving }
      );
      setTimeout(() => window.location.reload(), 800);
    } catch (error) {
      console.error("Error updating claimability:", error);
      const message =
        error?.response?.data?.error?.message || "Failed to update review.";
      toast.error(message, { id: saving });
    }
  };

  const closeDialog = () => {
    setClaimId(null);
    setSelectedPlayer(null);
    setDialog(false);
    window.location.reload();
  };

  const isMobile = useMediaQuery((theme) => theme.breakpoints.down("sm"));
  const NavTabs = [{ label: "Unclaimed" }, { label: "All" }];

  const renderClaimsBody = () => {
    if (!items || !items.length) return <NoClaims />;
    if (!filteredItems || !filteredItems.length) {
      return (
        <NoClaims
          title={unclaimedOnly ? "Every review is claimed" : "Nothing here yet"}
          subtitle={
            unclaimedOnly
              ? "Your team has claimed every available review. Switch to All to see the full history."
              : "Reviews will show up here as they come in."
          }
        />
      );
    }
    // Even grid — every card in a row shares the same height instead of
    // hugging its own content. A masonry wall reads fine on a wide website,
    // but on the app the uneven card heights look broken.
    return (
      <SoftBox
        sx={{
          display: "grid",
          gridTemplateColumns: {
            mini: "1fr",
            sm: "repeat(2, 1fr)",
            xl: "repeat(3, 1fr)",
          },
          gap: { mini: "14px", sm: "20px" },
          alignItems: "stretch",
        }}
      >
        {filteredItems.map((item) => (
          <ClaimNewCard
            key={item._id}
            handleClaim={handleClaim}
            item={item}
            assignPopUp={assignPopUp}
            handleUnassign={handleUnassign}
            handleSetClaimable={handleSetClaimable}
          />
        ))}
      </SoftBox>
    );
  };

  // The Unclaimed / All toggle. Rendered inside the header banner on every
  // breakpoint (via PageHeader's `tabs` prop, same as Team Feed's All
  // Feeds/My Jobs toggle) so mobile and desktop match.
  const tabsEl = (
    <Tabs
      orientation="horizontal"
      value={tabValue}
      onChange={(_, newValue) => setClaimsFilter(newValue)}
      sx={{
        background: "#fff!important",
        borderRadius: "8px",
        padding: "4px",
        border: "1px solid #EAECF0",
        width: "max-content",
        minHeight: "unset",
        "& .MuiTabs-indicator": {
          borderRadius: "6px",
          boxShadow: "none",
        },
      }}
    >
      {NavTabs.map((nav) => (
        <Tab
          key={nav.label}
          label={nav.label}
          sx={{
            color: "#343A40",
            fontSize: "0.8rem",
            minHeight: "32px",
            py: "6px",
            px: "16px",
          }}
        />
      ))}
    </Tabs>
  );

  return (
    <>
      <PageHeader
        headerText="Claims"
        subheaderText="Claim unclaimed reviews and showcase positive customer experiences"
        isMobile={isMobile}
        tabs={tabsEl}
      />

      <SoftBox
        sx={{
          margin: {
            mini: "0.5rem 1rem 1rem 1rem",
            sm: "1rem 1.5rem 1.5rem 1.5rem",
          },
        }}
      >
        {dialog && (
          <Modal open={dialog} onClose={() => {}} BackdropComponent={Backdrop} BackdropProps={{ timeout: 500 }}>
            <Fade in={dialog}>
              <SoftBox sx={{ ...styleModal, padding: "28px" }}>
                <SoftBox display="flex" flexDirection="column" alignItems="center" textAlign="center">
                  <span
                    style={{
                      width: 48,
                      height: 48,
                      borderRadius: "50%",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      background: T.goodSoft,
                      color: T.good,
                    }}
                  >
                    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M20 6 9 17l-5-5" />
                    </svg>
                  </span>
                  <SoftTypography sx={{ fontFamily: CLAIM_FONT, fontSize: "18px", fontWeight: 700, color: T.ink, mt: "16px", letterSpacing: "-0.01em" }}>
                    Review assigned
                  </SoftTypography>
                  <SoftTypography sx={{ fontFamily: CLAIM_FONT, fontSize: "13.5px", fontWeight: 400, color: T.muted, lineHeight: 1.5, mt: "6px", maxWidth: "32ch" }}>
                    {selectedPlayer && selectedPlayer.length
                      ? `Now credited to ${selectedPlayer.map((p) => `${p.firstName} ${p.lastName}`).join(", ")}.`
                      : "The review has been assigned."}
                  </SoftTypography>
                  <SoftButton
                    variant="contained"
                    color="primary"
                    onClick={closeDialog}
                    sx={{ fontFamily: CLAIM_FONT, textTransform: "none", borderRadius: "10px", fontWeight: 700, mt: "22px", minWidth: "160px" }}
                  >
                    Done
                  </SoftButton>
                </SoftBox>
              </SoftBox>
            </Fade>
          </Modal>
        )}
        <Modal open={assignModal} onClose={handleCloseModal} BackdropComponent={Backdrop} BackdropProps={{ timeout: 500 }}>
          <Fade in={assignModal}>
            <SoftBox sx={{ ...styleModal, padding: "24px" }}>
              <SoftBox display="flex" justifyContent="space-between" alignItems="flex-start" gap={1}>
                <SoftBox>
                  <SoftTypography sx={{ fontFamily: CLAIM_FONT, fontWeight: 700, fontSize: "17px", color: T.ink, letterSpacing: "-0.01em" }}>
                    Assign this review
                  </SoftTypography>
                  <SoftTypography sx={{ fontFamily: CLAIM_FONT, fontWeight: 400, fontSize: "13px", color: T.muted, mt: "3px" }}>
                    Credit it to one or more players in this workspace.
                  </SoftTypography>
                </SoftBox>
                <button
                  type="button"
                  onClick={handleCloseModal}
                  aria-label="Close"
                  style={{ display: "flex", flexShrink: 0, border: "none", background: "rgba(15,23,42,0.05)", borderRadius: "50%", padding: "5px", cursor: "pointer", color: T.muted }}
                >
                  <CloseIcon sx={{ fontSize: "1.15rem" }} />
                </button>
              </SoftBox>

              <SoftBox mt="18px">
                <SoftTypography sx={{ fontFamily: CLAIM_FONT, fontSize: "12.5px", fontWeight: 600, color: T.ink2, mb: "6px" }}>
                  Player
                </SoftTypography>
                <SoftSelect
                  name="isType"
                  id="category"
                  className="custom-select-catgory"
                  options={users}
                  isMulti
                  onChange={(option) => setSelectedPlayer([...option])}
                  value={selectedPlayer}
                  placeholder="Search players…"
                />
              </SoftBox>

              <SoftBox display="flex" gap={1.5} mt="24px">
                <SoftButton
                  fullWidth
                  color="white"
                  variant="outlined"
                  onClick={handleCloseModal}
                  sx={{
                    fontFamily: CLAIM_FONT,
                    textTransform: "none",
                    borderRadius: "10px",
                    fontWeight: 600,
                    color: `${T.ink2} !important`,
                    borderColor: "#D9DEE7 !important",
                    backgroundColor: "rgba(255,255,255,0.55) !important",
                    "&:hover": { backgroundColor: "rgba(255,255,255,0.85) !important" },
                  }}
                >
                  Cancel
                </SoftButton>
                <SoftButton
                  fullWidth
                  variant="contained"
                  color="primary"
                  onClick={assignClaim}
                  disabled={isAssigning}
                  sx={{ fontFamily: CLAIM_FONT, textTransform: "none", borderRadius: "10px", fontWeight: 700 }}
                >
                  {isAssigning ? "Assigning…" : "Assign review"}
                </SoftButton>
              </SoftBox>
            </SoftBox>
          </Fade>
        </Modal>
        {renderClaimsBody()}
      </SoftBox>
    </>
  );
};

export default ClaimsPage;
